import {
  BuildNode,
  Change,
  comparePayloads,
  NodeId,
  TreeView,
  TreeViewNode,
  StablePlace,
  StableRange,
  TraitLabel,
  TraitLocation,
  SharedTree,
} from '@fluid-experimental/tree';
import { getTypedNode, MfsEdgeNode } from '.';
import { MfsRelationship, MfsHost, isMfsItem, MfsItemId, MfsItem, MfsRelationshipQuery } from '../..';
import { assertPrecondition, mfsError } from '../../errors';
import { logVerbose } from '../../log';

import {
  EdgeLabelType,
  getEdgeLabelFilter,
  getUniqueLabelOf,
  INCOMING_TWO_WAY_EDGE,
  OUTGOING_TWO_WAY_EDGE,
  reverseDirectionOf,
  toMfsRelationship,
} from '../../store/relations';
import { removeDuplicates } from '../../utility';
import { getItem } from './itemsRead';
import { getItemNode, getItemNodeId, getNodeTraits, itemExists } from './nodesRead';
import { isEdgeNode } from './typeGuards';
import { MfsEdgeData, NodeKind, NodeTraits } from './types';

/** Returns the unique label of the given `relationship`. */
const getUniqueTraitLabel = (app: MfsHost, relationship: MfsRelationship) =>
  getUniqueLabelOf(app, relationship) as TraitLabel;

/** Adds a one-way relationship from the source `itemId` to each `relatedItemId` in batch mode. */
export function addOneWayRelation(
  tree: SharedTree,
  view: TreeView,
  itemId: MfsItemId,
  relationship: MfsRelationship,
  relatedItemIds: MfsItemId[],
  app: MfsHost
): Change[] {
  const itemNodeId = getItemNodeId(tree, view, itemId);
  logVerbose('addOneWayRelation', `Adding one way relationship itemNodeId `, {
    itemNodeId,
    relationshipDirection: relationship.direction,
  });
  return addForwardLink(tree, view, itemNodeId, relatedItemIds, relationship, app);
}

/**
 * Adds a two-way relationship from the source `itemId` to each `relatedItemId` as well as the reverse links from each
 * relatedItemId to the source itemId.
 */
export function addTwoWayRelation(
  tree: SharedTree,
  view: TreeView,
  itemId: MfsItemId,
  relationship: MfsRelationship,
  relatedItemIds: MfsItemId[],
  app: MfsHost
): Change[] {
  const dedupedRelatedItemIds = removeDuplicates(relatedItemIds);
  const changes = addOneWayRelation(tree, view, itemId, relationship, dedupedRelatedItemIds, app);
  const reverseLink = reverseDirectionOf(relationship);
  changes.push(...dedupedRelatedItemIds.flatMap((id) => addOneWayRelation(tree, view, id, reverseLink, [itemId], app)));
  logVerbose('addTwoWayRelation', `Adding two way relationship to itemNodeIds`, { dedupedRelatedItemIds });
  return changes;
}

/** Removes the relations in batch mode. */
export function removeOneWayRelation(
  tree: SharedTree,
  view: TreeView,
  itemId: MfsItemId,
  relationship: MfsRelationship,
  relatedItemIds: MfsItemId[],
  app: MfsHost
): Change[] {
  logVerbose('removeOneWayRelation', `Removing one way relationship from itemId`, { itemId });
  return removeLinks(tree, view, itemId, relatedItemIds, relationship, app, 'forward');
}

/** Removes the relations in batch mode. */
export function removeTwoWayRelation(
  tree: SharedTree,
  view: TreeView,
  itemId: MfsItemId,
  relationship: MfsRelationship,
  relatedItemIds: MfsItemId[],
  app: MfsHost
): Change[] {
  const dedupedRelatedItemIds = removeDuplicates(relatedItemIds);
  const changes = removeLinks(tree, view, itemId, dedupedRelatedItemIds, relationship, app, 'forward');

  if (changes.length === 0) {
    // if there's no forward link, the relationship does not exist, therefore no need to check the reverse links.
    logVerbose('removeTwoWayRelation', `No relationship to remove`, { itemId });
    return changes;
  }

  const reverseLink = reverseDirectionOf(relationship);
  changes.push(
    ...dedupedRelatedItemIds.flatMap((id) => removeLinks(tree, view, id, [itemId], reverseLink, app, 'reverse'))
  );
  logVerbose('removeTwoWayRelation', `Removing two way relationship`, { dedupedRelatedItemIds });
  return changes;
}

/** Adds the given relationship in its specified direction. */
export function addForwardLink(
  tree: SharedTree,
  view: TreeView,
  sourceNodeId: NodeId,
  destinationItemIds: readonly MfsItemId[],
  relationship: MfsRelationship,
  app: MfsHost
): Change[] {
  const edgeLabel = getUniqueTraitLabel(app, relationship);
  const edgeSetNode = getEdgeSetNode(view, sourceNodeId, edgeLabel);
  const destinationNodeIdsDeduped = removeDuplicates(destinationItemIds);

  if (!edgeSetNode) {
    return createEdgeSet(tree, sourceNodeId, edgeLabel, destinationNodeIdsDeduped, relationship);
  }
  return putEdgeSet(view, edgeSetNode, destinationNodeIdsDeduped, relationship);
}

/** Creates the edge set node that contains relatedItemIds as trait keys and edge node as values. */
export function createEdgeSet(
  tree: SharedTree,
  sourceNodeId: NodeId,
  edgeLabel: TraitLabel,
  destinationNodeIds: readonly MfsItemId[],
  relationship: MfsRelationship
): Change[] {
  const edgeSetId = tree.generateNodeId();

  const changes = Change.insertTree(
    [makeEdgeSetNode(edgeSetId)],
    StablePlace.atStartOf({ parent: sourceNodeId, label: edgeLabel })
  );

  for (const destinationNodeId of destinationNodeIds) {
    const edgeData = makeEdgeData(relationship, destinationNodeId);
    changes.push(...insertEdge(edgeSetId, edgeData));
  }
  logVerbose('createEdgeSet', `Creating edge set`, { sourceNodeId, destinationNodeIds, relationship });
  return changes;
}

/**
 * When an app attempts to recreate an existing edge, we let it override the previous one. There's no preservation of
 * prior values in this case, all edge properties are immutable, so this works like a replace (put).
 */
export function putEdgeSet(
  view: TreeView,
  edgeSetNode: TreeViewNode,
  destinationIds: readonly MfsItemId[],
  relationship: MfsRelationship
): Change[] {
  const changes: Change[] = [];

  for (const destinationId of destinationIds) {
    const edgeData = makeEdgeData(relationship, destinationId);

    if (shouldUpdate(view, edgeSetNode, destinationId, edgeData)) {
      changes.push(...insertEdge(edgeSetNode.identifier, edgeData));
    }
  }
  logVerbose('putEdgeSet', `Overriding existing edge set`, {
    edgeSetNode,
    destinationIds,
    relationship,
  });
  return changes;
}

/** Compare edge data to avoid redundant change notifications when a relationship is attempted to be re-added. */
function shouldUpdate(
  view: TreeView,
  edgeSetNode: TreeViewNode,
  destinationId: MfsItemId,
  edgeData: MfsEdgeData
): boolean {
  const edgeTrait = destinationId as string as TraitLabel;
  if (!edgeSetNode.traits.has(edgeTrait)) {
    // ok, it doesn't already exist.
    return true;
  }

  // only update if the data is different than existing
  const edgeNode = getEdgeNode(view, edgeSetNode, destinationId);
  return !comparePayloads(edgeNode.payload, edgeData);
}

/** Removes the given relationship (in its specified direction) in batch mode. */
export function removeLinks(
  tree: SharedTree,
  view: TreeView,
  itemId: MfsItemId,
  destinationIds: readonly MfsItemId[],
  relationship: MfsRelationship,
  app: MfsHost,
  mode: 'forward' | 'reverse'
): Change[] {
  // ok to skip when removing reverse links
  if (mode === 'reverse' && !itemExists(tree, view, itemId)) {
    logVerbose('removeLinks', `source itemId: ${itemId} not found`);
    return [];
  }

  const sourceNodeId = getItemNodeId(tree, view, itemId);
  const edgeLabel = getUniqueTraitLabel(app, relationship);
  const edgeSetId = getEdgeSetNodeId(view, sourceNodeId, edgeLabel);

  if (!edgeSetId) {
    logVerbose('removeLinks', `No edges to remove with type`);
    return [];
  }

  return destinationIds.map((destinationNodeId) => removeEdge(edgeSetId, destinationNodeId));
}

function makeEdgeData(relationship: MfsRelationship, neighbor: MfsItemId): MfsEdgeData {
  return {
    type: relationship.type,
    neighbor,
    dir: relationship.direction,
    ...(relationship.attributes && { attributes: relationship.attributes }),
  };
}

function makeEdgeNode(edgeData: MfsEdgeData): BuildNode {
  return {
    definition: NodeKind.Edge,
    payload: edgeData,
  };
}

function makeEdgeSetNode(identifier: NodeId): BuildNode {
  return { identifier, definition: NodeKind.EdgeSet };
}

function insertEdge(edgeSetNodeId: NodeId, edgeData: MfsEdgeData): Change[] {
  const location: TraitLocation = { parent: edgeSetNodeId, label: edgeData.neighbor as unknown as TraitLabel };
  // always delete & recreate to prevent duplicate edge nodes.
  return [
    Change.delete(StableRange.all(location)),
    ...Change.insertTree([makeEdgeNode(edgeData)], StablePlace.atStartOf(location)),
  ];
}

function removeEdge(edgeSetNodeId: NodeId, neighbor: MfsEdgeData['neighbor']): Change {
  const location: TraitLocation = { parent: edgeSetNodeId, label: neighbor as unknown as TraitLabel };
  return Change.delete(StableRange.all(location));
}

function getEdgeSetNodeId(view: TreeView, parent: NodeId, edgeLabel: TraitLabel): NodeId | undefined {
  const traits = view.getTrait({ parent, label: edgeLabel });
  return traits.length === 0 ? undefined : traits[0];
}

function getEdgeSetNode(view: TreeView, parent: NodeId, edgeLabel: TraitLabel): TreeViewNode | undefined {
  const existingId = getEdgeSetNodeId(view, parent, edgeLabel);
  return existingId ? view.getViewNode(existingId) : undefined;
}

function getEdgeNode(view: TreeView, edgeSetNode: TreeViewNode, neighbor: MfsEdgeData['neighbor']): MfsEdgeNode {
  const existingEdgeId = (edgeSetNode.traits.get(neighbor as unknown as TraitLabel) ?? [])[0];
  assertPrecondition(!!existingEdgeId);
  return getTypedNode<MfsEdgeNode>(view, existingEdgeId, isEdgeNode);
}

/** @yields The related Mfs items. */
export function* getRelatedItems(
  tree: SharedTree,
  view: TreeView,
  app: MfsHost,
  itemId: MfsItemId,
  relationship: MfsRelationship
): IterableIterator<MfsItem> {
  const itemNodeId = getItemNodeId(tree, view, itemId);
  const edgeLabel = getUniqueTraitLabel(app, relationship);
  const edgeSetNodeId = getEdgeSetNodeId(view, itemNodeId, edgeLabel);

  if (!edgeSetNodeId) {
    logVerbose('getRelatedItems', `No related items found`, { itemId });
    return;
  }

  const edgeSetNode = view.getViewNode(edgeSetNodeId);

  for (const relatedItemId of edgeSetNode.traits.keys()) {
    const item = getItem(tree, view, relatedItemId as unknown as MfsItemId, isMfsItem);

    if (item) {
      logVerbose('getRelatedItems', `Found relatedItem: ${item.id}`);
      yield item;
    } else {
      logVerbose('getRelatedItems', `Skipped relatedItem: ${itemId}.`);
    }
  }
}

/** @yields The Mfs relationships. */
export function* getAllRelationships(
  tree: SharedTree,
  view: TreeView,
  itemId: MfsItemId,
  query?: MfsRelationshipQuery
): IterableIterator<{ relatedItemId: MfsItemId; relationship: MfsRelationship }> {
  const itemNodeId = getItemNodeId(tree, view, itemId);
  const allEdgeLabels = getNodeTraits(view, itemNodeId, getEdgeLabelFilter(query?.filter));

  for (const edgeLabel of allEdgeLabels.keys()) {
    const edgeSetNode = getEdgeSetNode(view, itemNodeId, edgeLabel);
    assertPrecondition(!!edgeSetNode);

    for (const [relatedItemId, edgeNodes] of edgeSetNode.traits) {
      const edgeNode = getTypedNode<MfsEdgeNode>(view, edgeNodes[0], isEdgeNode);
      const filter = query?.filter;
      const attributes = edgeNode.payload.attributes;
      if (!filter || !filter.matchAttributes || filter.matchAttributes(attributes)) {
        const relationship = toMfsRelationship(edgeLabel as EdgeLabelType, attributes);
        yield { relatedItemId: relatedItemId as unknown as MfsItemId, relationship };
      }
    }
  }
}

// UTILS FOR ITEM DELETION

/** The forward links are removed when the parent item is deleted, but we need to find & remove the reverse links. */
export function removeAllReverseLinks(
  tree: SharedTree,
  view: TreeView,
  itemId: MfsItemId,
  itemNodeId: NodeId
): Change[] {
  const forwardSets = getAllTwoWayEdgeSets(view, itemNodeId);
  const changes: Change[] = [];

  for (const [edgeLabel, edgeSetTraits] of forwardSets) {
    const edgeSetNode = view.getViewNode(edgeSetTraits[0]);

    for (const neighbor of edgeSetNode.traits.keys()) {
      const reverseEdgeSetTraits = getItemNode(tree, view, neighbor as unknown as MfsItemId).traits.get(
        reverseTraitOf(edgeLabel)
      );

      if (!reverseEdgeSetTraits) {
        continue;
      }

      changes.push(
        Change.delete(StableRange.all({ parent: reverseEdgeSetTraits[0], label: itemId as unknown as TraitLabel }))
      );
    }
  }
  return changes;
}

const getAllTwoWayEdgeSets = (view: TreeView, sourceNodeId: NodeId): NodeTraits =>
  getNodeTraits(
    view,
    sourceNodeId,
    (key) => key.startsWith(OUTGOING_TWO_WAY_EDGE) || key.startsWith(INCOMING_TWO_WAY_EDGE)
  );

function reverseTraitOf(edgeLabel: TraitLabel): TraitLabel {
  if (edgeLabel.startsWith(OUTGOING_TWO_WAY_EDGE)) {
    return edgeLabel.replace('#e.out', '#e.in') as TraitLabel;
  }

  if (edgeLabel.startsWith(INCOMING_TWO_WAY_EDGE)) {
    return edgeLabel.replace('#e.in', '#e.out') as TraitLabel;
  }

  assertPrecondition(false, mfsError.invalidEdgeLabel);
}
